import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Search, Loader2, Home, User, Building2, Phone, Mail,
  FileText, CheckCircle, AlertCircle, Clock, Edit, Trash2, Plus,
  ChevronDown, ChevronUp, Link2, PoundSterling, Calendar
} from "lucide-react";
import { API } from "@/App";

const CHAIN_STATUS_OPTIONS = [
  { value: "no_chain", label: "No Chain", color: "bg-green-100 text-green-800" },
  { value: "chain_free", label: "Chain Free", color: "bg-green-100 text-green-800" },
  { value: "chain_complete", label: "Chain Complete", color: "bg-blue-100 text-blue-800" },
  { value: "chain_incomplete", label: "Chain Incomplete", color: "bg-amber-100 text-amber-800" },
  { value: "awaiting_info", label: "Awaiting Info", color: "bg-slate-100 text-slate-800" },
  { value: "ftb", label: "First Time Buyer", color: "bg-purple-100 text-purple-800" },
  { value: "investor", label: "Investor/Cash", color: "bg-emerald-100 text-emerald-800" }
];

const FINANCE_STATUS_OPTIONS = [
  { value: "cash", label: "Cash Buyer", color: "bg-green-100 text-green-800" },
  { value: "mortgage_aip", label: "Mortgage AIP", color: "bg-blue-100 text-blue-800" },
  { value: "mortgage_applied", label: "Mortgage Applied", color: "bg-amber-100 text-amber-800" },
  { value: "mortgage_offered", label: "Mortgage Offered", color: "bg-green-100 text-green-800" },
  { value: "awaiting_valuation", label: "Awaiting Valuation", color: "bg-orange-100 text-orange-800" },
  { value: "valuation_done", label: "Valuation Done", color: "bg-blue-100 text-blue-800" },
  { value: "proof_of_funds", label: "Proof of Funds Received", color: "bg-green-100 text-green-800" }
];

const SalesChainPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedNotes, setExpandedNotes] = useState({});
  
  // Note modal
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  
  // Edit note modal
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingCaseId, setEditingCaseId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch active and exchanged cases
      const casesRes = await axios.get(`${API}/sales/cases`, { headers });
      
      // Handle both array and {cases: [...]} response formats
      const casesData = Array.isArray(casesRes.data) ? casesRes.data : (casesRes.data.cases || []);
      
      // Filter for active/exchanged cases
      const activeCases = casesData.filter(c => 
        c.status === 'active' || c.status === 'exchanged'
      );
      
      // Fetch notes for all cases
      const casesWithNotes = await Promise.all(
        activeCases.map(async (c) => {
          try {
            const notesRes = await axios.get(`${API}/sales/cases/${c.case_id}/notes`, { headers });
            return { ...c, notes: notesRes.data.notes || [] };
          } catch {
            return { ...c, notes: [] };
          }
        })
      );
      
      setCases(casesWithNotes);
    } catch (error) {
      console.error("Error fetching chain data:", error);
      toast.error("Failed to load chain data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedCase) return;
    
    setSavingNote(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/sales/cases/${selectedCase.case_id}/notes`, {
        content: newNote,
        note_type: "general"
      }, { headers });
      
      toast.success("Note added");
      setNewNote("");
      setShowNoteModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleEditNote = async () => {
    if (!editingNote?.content?.trim() || !editingCaseId) return;
    
    setSavingNote(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/sales/cases/${editingCaseId}/notes/${editingNote.note_id}`, {
        content: editingNote.content
      }, { headers });
      
      toast.success("Note updated");
      setShowEditNoteModal(false);
      setEditingNote(null);
      setEditingCaseId(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (caseId, noteId) => {
    if (!confirm("Are you sure you want to delete this note?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/sales/cases/${caseId}/notes/${noteId}`, { headers });
      toast.success("Note deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const getParty = (parties, role) => {
    return parties?.find(p => p.role?.toLowerCase().includes(role.toLowerCase())) || null;
  };

  const getStatusBadge = (caseData) => {
    // Check for any issues/problems
    const hasProblems = caseData.has_issues || caseData.status === "at_risk";
    if (hasProblems) {
      return <Badge className="bg-red-500 text-white">ISSUES</Badge>;
    }
    return <Badge className="bg-green-500 text-white">NO PROBLEMS</Badge>;
  };

  const getChainStatusBadge = (status) => {
    const option = CHAIN_STATUS_OPTIONS.find(o => o.value === status);
    return option ? (
      <Badge className={option.color}>{option.label}</Badge>
    ) : (
      <Badge className="bg-slate-100 text-slate-600">Not Set</Badge>
    );
  };

  const getFinanceStatusBadge = (status) => {
    const option = FINANCE_STATUS_OPTIONS.find(o => o.value === status);
    return option ? (
      <Badge className={option.color}>{option.label}</Badge>
    ) : (
      <Badge className="bg-slate-100 text-slate-600">Not Set</Badge>
    );
  };

  const toggleNotes = (caseId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [caseId]: !prev[caseId]
    }));
  };

  const filteredCases = cases.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.address_line1?.toLowerCase().includes(query) ||
      c.postcode?.toLowerCase().includes(query) ||
      c.parties?.some(p => p.name?.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-full mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/sales/pipeline")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Pipeline
              </Button>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <Link2 className="w-6 h-6 text-indigo-600" />
                <h1 className="text-xl font-bold text-slate-800">Sales Chain View</h1>
              </div>
              <Badge variant="outline" className="ml-2">
                {filteredCases.length} Active Cases
              </Badge>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search address, postcode, name..."
                  className="pl-9 w-64"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 space-y-4">
        {filteredCases.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Home className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-muted-foreground">No active sales found</p>
            </CardContent>
          </Card>
        ) : (
          filteredCases.map((caseData) => {
            const vendor = getParty(caseData.parties, "seller") || getParty(caseData.parties, "vendor");
            const vendorSolicitor = getParty(caseData.parties, "seller_solicitor") || getParty(caseData.parties, "vendor_solicitor");
            const buyer = getParty(caseData.parties, "buyer");
            const buyerSolicitor = getParty(caseData.parties, "buyer_solicitor");
            const isNotesExpanded = expandedNotes[caseData.case_id];
            
            return (
              <Card key={caseData.case_id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex">
                    {/* Property Image Section */}
                    <div className="w-48 bg-slate-200 relative flex-shrink-0">
                      {caseData.main_photo ? (
                        <img 
                          src={caseData.main_photo} 
                          alt={caseData.address_line1}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-slate-100">
                          <Home className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        {getStatusBadge(caseData)}
                      </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 p-4">
                      {/* Header Row */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h2 className="text-lg font-bold text-slate-800">
                            {caseData.address_line1}
                          </h2>
                          <p className="text-sm text-slate-500">{caseData.postcode}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-emerald-100 text-emerald-800">
                            <PoundSterling className="w-3 h-3 mr-1" />
                            {caseData.offer_price?.toLocaleString() || caseData.asking_price?.toLocaleString() || "TBC"}
                          </Badge>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/sales/case/${caseData.case_id}/hub`)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </div>

                      {/* Two Column Layout for Vendor and Buyer */}
                      <div className="grid grid-cols-2 gap-6">
                        {/* Vendor Side */}
                        <div className="space-y-3">
                          <h3 className="font-semibold text-sm text-slate-600 border-b pb-1">VENDOR SIDE</h3>
                          
                          {/* Vendor */}
                          <div className="flex items-start gap-2">
                            <User className="w-4 h-4 text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Vendor</p>
                              <p className="font-medium">{vendor?.name || "Not set"}</p>
                              {vendor?.phone && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {vendor.phone}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Agent */}
                          <div className="flex items-start gap-2">
                            <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Agent</p>
                              <p className="font-medium">{caseData.assigned_agent || caseData.listing_agent || "Goodacres"}</p>
                            </div>
                          </div>

                          {/* Vendor Solicitor */}
                          <div className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Vendor Solicitor</p>
                              <p className="font-medium">{vendorSolicitor?.name || vendorSolicitor?.firm_name || "Not set"}</p>
                              {vendorSolicitor?.phone && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {vendorSolicitor.phone}
                                </p>
                              )}
                              {vendorSolicitor?.email && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {vendorSolicitor.email}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Vendor Chain Status */}
                          <div className="flex items-start gap-2">
                            <Link2 className="w-4 h-4 text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Vendor Chain Status</p>
                              {getChainStatusBadge(caseData.vendor_chain_status)}
                            </div>
                          </div>
                        </div>

                        {/* Buyer Side */}
                        <div className="space-y-3">
                          <h3 className="font-semibold text-sm text-slate-600 border-b pb-1">BUYER SIDE</h3>
                          
                          {/* Buyer */}
                          <div className="flex items-start gap-2">
                            <User className="w-4 h-4 text-blue-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Buyer</p>
                              <p className="font-medium">{buyer?.name || "Not set"}</p>
                              {buyer?.phone && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {buyer.phone}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Buyer Solicitor */}
                          <div className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-blue-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Buyer Solicitor</p>
                              <p className="font-medium">{buyerSolicitor?.name || buyerSolicitor?.firm_name || "Not set"}</p>
                              {buyerSolicitor?.phone && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {buyerSolicitor.phone}
                                </p>
                              )}
                              {buyerSolicitor?.email && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {buyerSolicitor.email}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Buyer Chain Status */}
                          <div className="flex items-start gap-2">
                            <Link2 className="w-4 h-4 text-blue-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Buyer Chain Status</p>
                              {getChainStatusBadge(caseData.buyer_chain_status)}
                            </div>
                          </div>

                          {/* Buyer Finance Status */}
                          <div className="flex items-start gap-2">
                            <PoundSterling className="w-4 h-4 text-blue-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Buyer Finance Status</p>
                              {getFinanceStatusBadge(caseData.buyer_finance_status)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Notes Section */}
                      <div className="mt-4 pt-3 border-t">
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => toggleNotes(caseData.case_id)}
                        >
                          <h3 className="font-semibold text-sm text-slate-600 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Notes ({caseData.notes?.length || 0})
                          </h3>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCase(caseData);
                                setShowNoteModal(true);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Note
                            </Button>
                            {isNotesExpanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        </div>
                        
                        {isNotesExpanded && (
                          <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                            {caseData.notes?.length > 0 ? (
                              caseData.notes.map((note, idx) => (
                                <div 
                                  key={note.note_id || idx} 
                                  className="p-3 bg-slate-50 rounded-lg text-sm"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium text-slate-700">
                                          {note.created_by_name || "Staff"}
                                        </span>
                                        <span className="text-xs text-slate-400">
                                          {note.created_at ? format(parseISO(note.created_at), "dd/MM/yyyy HH:mm") : ""}
                                          {note.updated_at && note.updated_at !== note.created_at && (
                                            <span> • Edited</span>
                                          )}
                                        </span>
                                      </div>
                                      <p className="text-slate-600">{note.content}</p>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingNote(note);
                                          setEditingCaseId(caseData.case_id);
                                          setShowEditNoteModal(true);
                                        }}
                                        data-testid={`edit-chain-note-${idx}`}
                                      >
                                        <Edit className="w-3.5 h-3.5 text-slate-400" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 hover:bg-red-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteNote(caseData.case_id, note.note_id);
                                        }}
                                        data-testid={`delete-chain-note-${idx}`}
                                      >
                                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-slate-400 italic">No notes yet</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </main>

      {/* Add Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          {selectedCase && (
            <p className="text-sm text-muted-foreground">
              {selectedCase.address_line1}, {selectedCase.postcode}
            </p>
          )}
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Enter your note..."
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNote} disabled={savingNote || !newNote.trim()}>
              {savingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Note Modal */}
      <Dialog open={showEditNoteModal} onOpenChange={(open) => {
        setShowEditNoteModal(open);
        if (!open) {
          setEditingNote(null);
          setEditingCaseId(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
          </DialogHeader>
          <Textarea
            value={editingNote?.content || ""}
            onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
            placeholder="Enter your note..."
            rows={4}
            data-testid="edit-chain-note-content"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditNoteModal(false); setEditingNote(null); setEditingCaseId(null); }}>
              Cancel
            </Button>
            <Button onClick={handleEditNote} disabled={savingNote || !editingNote?.content?.trim()}>
              {savingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesChainPage;
